export interface IReassessmentsProps {}

export interface SearchPendingReassessmentLinesRequest {
  periodName: string;
  jurisdiction: string;
}

export interface PendingReassessmentLine {
  transactionStatus: string;
  periodName: string;
  enteredCurrencyCode: string;
  localCurrencyCode: string;
  finalFXRateDate: string;
}

export interface SearchPendingReassessmentLinesResponse {
  lines: PendingReassessmentLine[];
}

export interface RegisterManualFXRatesRequest {
  requestedByUser: string;
  fxRateFileS3Key: string;
  jurisdiction: string;
  periodName?: string;
  uploadForDates?: string[];
  comment?: string;
}

/** Response from registering manual FX rates via file upload */
export interface RegisterManualFXRatesResponse {
  requestId: string;
  requestStatus: string;
}

/** Individual FX rate entry for manual submission */
export interface ManualFXRateEntry {
  /** Source currency code (e.g. USD, EUR) */
  fromCurrency: string;
  /** Target currency code (e.g. USD, EUR) */
  toCurrency: string;
  /** Conversion rate value */
  rate: string;
  /** Date of the conversion rate */
  conversionDate: string;
}

/** Request to register manually entered FX rates */
export interface RegisterManualFXRatesEntryRequest {
  requestedByUser: string;
  jurisdiction: string;
  evidenceFileS3Key: string;
  rates: ManualFXRateEntry[];
  comment?: string;
}

/** Response from registering manually entered FX rates */
export interface RegisterManualFXRatesEntryResponse {
  requestId: string;
  requestStatus: ManualFXRatesRequestStatus;
}

/** Request to approve or submit FX rates for processing */
export interface ApproveManualFXRatesRequest {
  requestId: string;
  creationTime: number;
  editedRates?: ParsedFXRate[];
}

/** Parsed FX rate record returned from the backend */
export interface ParsedFXRate {
  conversionType?: string;
  fromCurrency?: string;
  toCurrency?: string;
  conversionRate?: string;
  conversionDate?: string;
  statusCode?: string;
  comment?: string;
  /** True if the submitter modified this rate from the originally parsed value. */
  edited?: boolean;
}

/** Response from approving FX rates */
export interface ApproveManualFXRatesResponse {
  requestId: string;
  requestStatus: ManualFXRatesRequestStatus;
}

// Legacy interface for backward compatibility
export interface Lines {
  id?: string;
  periodName?: string;
  payorJurisdiction?: string;
  [key: string]: any;
}

/** Status values for manual FX rates requests (matches backend enum) */
export enum ManualFXRatesRequestStatus {
  /** Initial status when the request is registered but not yet processed */
  REGISTERED = "REGISTERED",
  /** Status when the FX rates file is being parsed and validated */
  PARSING_IN_PROGRESS = "PARSING_IN_PROGRESS",
  /** Status when the FX rates file parsing has been completed successfully */
  PARSING_COMPLETED = "PARSING_COMPLETED",
  /** Status when the system is updating FX rates in the database */
  UPDATING_FX_RATES = "UPDATING_FX_RATES",
  /** Status when the entire manual FX rates process has completed successfully */
  SUCCEEDED = "SUCCEEDED",
  /** Status when the processing failed due to an error at any stage */
  FAILED = "FAILED",
  /** Status when rates are pending approval */
  PENDING_APPROVAL = "PENDING_APPROVAL",
  /** Status when rates have been approved */
  APPROVED = "APPROVED",
}

/**
 * Represents a manual FX rate submission record
 */
export interface MySubmission {
  /** Unique identifier for the submission request */
  requestId: string;
  /** Timestamp when the submission was created (epoch milliseconds) */
  creationTime: number;
  /** Formatted display string for creationTime */
  creationTimeDisplay?: string;
  /** User alias who submitted the FX rates */
  requestedByUser: string;
  /** Year for which the FX rates were submitted */
  fxRateYear: number;
  /** Month for which the FX rates were submitted (MMM format) */
  fxRateMonth: string;
  /** Jurisdiction/region for the FX rates */
  jurisdiction: string;
  /** Pre-signed URL for downloading the uploaded FX rates file */
  presignedDownloadUrl: string;
  /** Current processing status of the submission */
  status: ManualFXRatesRequestStatus;
  /** Status message for the Request*/
  statusMessage?: string;
  /** List of dates for which the FX rates were uploaded */
  uploadForDates: string[];
  /** JSON string of parsed FX rates */
  parsedRates?: ParsedFXRate[];
  /** Optional comment provided during manual rate entry */
  comment?: string;
}

export interface GetMySubmissionsResponse {
  submissions: MySubmission[];
}

/** Response from GetFXRatePendingApprovals API with pagination support. */
export interface GetFXRatePendingApprovalsResponse {
  submissions: MySubmission[];
  nextToken?: string;
}

/** Response from GetReassessmentJurisdictions API */
export interface GetReassessmentJurisdictionsResponse {
  jurisdictions: string[];
}
